import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import SplashScreen from '@/components/SplashScreen';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Coproprietaires from '@/pages/Coproprietaires';
import AssembleesGenerales from '@/pages/AssembleesGeneralesV2';
import AppelsFonds from '@/pages/AppelsFondsV3';
import Documents from '@/pages/Documents';
import Profil from '@/pages/Profil';
import CreerCopropriete from '@/pages/CreerCoproprieteV2';
import SelectionCopropriete from '@/pages/SelectionCopropriete';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PaymentCancel from '@/pages/PaymentCancel';
import AdminDashboard from '@/pages/AdminDashboard';
import AssistantJuridique from '@/pages/AssistantJuridique';
import SujetsAG from '@/pages/SujetsAG';
import Calendrier from '@/pages/Calendrier';
import Comptabilite from '@/pages/Comptabilite';
import LandingPage from '@/pages/LandingPage';
import BlogList from '@/pages/BlogList';
import BlogArticle from '@/pages/BlogArticle';
import BlogAdmin from '@/pages/BlogAdmin';
import ActivateTrialPage from '@/pages/ActivateTrialPage';
// SEO Pages
import SyndicBenevole from '@/pages/seo/SyndicBenevole';
import AssembleeGenerale from '@/pages/seo/AssembleeGenerale';
import ChargesCopropriete from '@/pages/seo/ChargesCopropriete';
import BudgetPrevisionnel from '@/pages/seo/BudgetPrevisionnel';
import ReglementCopropriete from '@/pages/seo/ReglementCopropriete';
import AppelDeFondsModele from '@/pages/seo/AppelDeFondsModele';
import ConvocationAGModele from '@/pages/seo/ConvocationAGModele';
import GestionCopropriete from '@/pages/seo/GestionCopropriete';
import FAQPage from '@/pages/seo/FAQPage';
import LogicielSyndicGratuit from '@/pages/seo/LogicielSyndicGratuit';
import AppGestionCopropriete from '@/pages/seo/AppGestionCopropriete';
import OutilGestionPage from '@/pages/seo/OutilGestionPage';

import MentionsLegales from '@/pages/legal/MentionsLegales';
import CGU from '@/pages/legal/CGU';
import PolitiqueConfidentialite from '@/pages/legal/PolitiqueConfidentialite';

// Pages SEO Impayés
import ImpayesCopropriete from '@/pages/ImpayesCopropriete';
import GenerateurMiseEnDemeure from '@/pages/GenerateurMiseEnDemeure';
import GestionImpayes from '@/pages/GestionImpayes';
import PaiementSucces from '@/pages/PaiementSucces';
import HistoriquePaiements from '@/pages/HistoriquePaiements';

import Layout from '@/components/Layout';
import PaymentModal from '@/components/PaymentModal';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Context pour gérer l'affichage de la modal de paiement
export const PaymentContext = createContext();

export const usePayment = () => useContext(PaymentContext);

export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Variable pour stocker la fonction d'ouverture de la modal de paiement
let openPaymentModalFn = null;

export const setPaymentModalOpener = (fn) => {
  openPaymentModalFn = fn;
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Intercepteur 402 - Abonnement requis
    if (error.response?.status === 402) {
      // Déclencher l'ouverture du modal de paiement via un événement custom
      window.dispatchEvent(new CustomEvent('premium-required', { 
        detail: error.response?.data?.detail 
      }));
    }
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour extraire le message d'erreur
export const getErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  // Si c'est une erreur 402, ne pas afficher de message (géré par l'intercepteur)
  if (error.response?.status === 402) {
    return null;
  }
  
  const detail = error.response?.data?.detail;
  
  // Si detail est un objet avec un champ message
  if (detail && typeof detail === 'object' && detail.message) {
    return detail.message;
  }
  
  // Si detail est une chaîne
  if (detail && typeof detail === 'string') {
    return detail;
  }
  
  return defaultMessage;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  // Vérifier si on est en mode PWA/standalone (app installée)
  const isPWA = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      
      // Récupérer les infos utilisateur fraîches depuis le backend
      // pour s'assurer que must_change_password est à jour
      fetchCurrentUser();
    }
    setLoading(false);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      const freshUser = response.data;
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch (error) {
      // En cas d'erreur (token invalide), on garde l'utilisateur local
      console.error('Erreur récupération utilisateur:', error);
    }
  };

  // Enregistrer la fonction d'ouverture de la modal
  useEffect(() => {
    setPaymentModalOpener(() => setPaymentModalOpen(true));
  }, []);

  // Écouter l'événement premium-required pour ouvrir le modal de paiement
  useEffect(() => {
    const handlePremiumRequired = (event) => {
      console.log('Premium required event:', event.detail);
      setPaymentModalOpen(true);
    };
    
    window.addEventListener('premium-required', handlePremiumRequired);
    return () => {
      window.removeEventListener('premium-required', handlePremiumRequired);
    };
  }, []);

  // Récupérer le statut d'abonnement et le statut admin
  useEffect(() => {
    if (user) {
      if (user.copropriete_active_id) {
        fetchSubscriptionStatus();
      }
      checkAdminStatus();
    }
  }, [user?.copropriete_active_id, user?.email]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await apiClient.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Erreur récupération statut abonnement:', error);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await apiClient.get('/admin/check');
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    setIsAdmin(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Déconnexion réussie');
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Vérifier si l'utilisateur est le Master Admin
  const isMasterAdmin = user && user.email?.toLowerCase() === 'gmpdeveloppement@gmail.com';

  // Vérifier si l'utilisateur a une copropriété active (pas nécessaire pour Master Admin)
  const hasCopropriete = user && user.coproprietes && user.coproprietes.length > 0;

  // Vérifier si l'utilisateur doit changer son mot de passe
  const mustChangePassword = user && user.must_change_password === true;

  const paymentContextValue = {
    subscriptionStatus,
    openPaymentModal: () => setPaymentModalOpen(true),
    refreshSubscription: fetchSubscriptionStatus
  };

  return (
    <HelmetProvider>
    <PaymentContext.Provider value={paymentContextValue}>
      {/* Splash Screen - affiché uniquement en mode PWA/app */}
      {showSplash && isPWA && (
        <SplashScreen onFinish={() => setShowSplash(false)} />
      )}
      
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
            }
          />
          <Route
            path="/selection-copropriete"
            element={
              user ? (
                <SelectionCopropriete user={user} onUpdate={updateUser} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/activer-essai"
            element={
              user ? (
                hasCopropriete ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <ActivateTrialPage user={user} onTrialActivated={fetchSubscriptionStatus} onLogout={handleLogout} />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/creer-copropriete"
            element={
              user ? (
                <CreerCopropriete user={user} onUpdate={updateUser} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/abonnement/success"
            element={
              user ? (
                <PaymentSuccess onUpdate={() => { fetchSubscriptionStatus(); updateUser(user); }} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/abonnement/cancel"
            element={
              user ? <PaymentCancel /> : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/paiement-succes"
            element={<PaiementSucces />}
          />
          <Route
            path="/historique-paiements"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)}>
                  <HistoriquePaiements user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && (isAdmin || isMasterAdmin) ? (
                <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={true}>
                  <AdminDashboard />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                isMasterAdmin ? (
                  <Navigate to="/admin" replace />
                ) : mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : hasCopropriete ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Navigate to="/activer-essai" replace />
                )
              ) : (
                <LandingPage />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : hasCopropriete ? (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <Dashboard user={user} />
                  </Layout>
                ) : (
                  <Navigate to="/activer-essai" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/blog" element={<BlogList user={user} />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route
            path="/admin/blog"
            element={
              user ? (
                <BlogAdmin user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* SEO Pages - Structure Wikipédia de la copropriété */}
          <Route path="/syndic-benevole" element={<SyndicBenevole />} />
          <Route path="/assemblee-generale-copropriete" element={<AssembleeGenerale />} />
          <Route path="/charges-copropriete" element={<ChargesCopropriete />} />
          <Route path="/budget-previsionnel-copropriete" element={<BudgetPrevisionnel />} />
          <Route path="/reglement-de-copropriete" element={<ReglementCopropriete />} />
          <Route path="/appel-de-fonds-modele" element={<AppelDeFondsModele />} />
          <Route path="/convocation-ag-modele" element={<ConvocationAGModele />} />
          <Route path="/gestion-copropriete" element={<GestionCopropriete />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/logiciel-syndic-gratuit" element={<LogicielSyndicGratuit />} />
          <Route path="/application-gestion-copropriete" element={<AppGestionCopropriete />} />
          <Route path="/outil-gestion-copropriete" element={<OutilGestionPage />} />
          
          {/* Pages SEO Impayés */}
          <Route path="/impayes-copropriete" element={<ImpayesCopropriete />} />
          <Route path="/generateur-mise-en-demeure" element={<GenerateurMiseEnDemeure />} />
          
          {/* Pages legales */}
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
          
          <Route
            path="/coproprietaires"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <Coproprietaires user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/assemblees"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <AssembleesGenerales user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/calendrier"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <Calendrier user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/gestion-impayes"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <GestionImpayes user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/appels-fonds"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <AppelsFonds user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/documents"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <Documents user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/assistant"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <AssistantJuridique user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/comptabilite"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <Comptabilite user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/sujets-ag"
            element={
              user && hasCopropriete ? (
                mustChangePassword ? (
                  <Navigate to="/profil" replace />
                ) : (
                  <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                    <SujetsAG user={user} />
                  </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/profil"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} onUpdate={updateUser} subscriptionStatus={subscriptionStatus} onOpenPayment={() => setPaymentModalOpen(true)} isAdmin={isAdmin}>
                  <Profil user={user} setUser={setUser} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      
      {/* Modal de paiement */}
      {user && user.copropriete_active_id && (
        <PaymentModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          coproprieteId={user.copropriete_active_id}
          onSubscriptionUpdate={fetchSubscriptionStatus}
        />
      )}
      
      <Toaster position="top-right" richColors />
    </PaymentContext.Provider>
    </HelmetProvider>
  );
}

export default App;